/**
 * 暦の日付（`YYYY-MM-DD`）の判定と加減、日本時間の日時（`YYYY-MM-DD HH:mm:ss`）の読み書き。
 *
 * 日次バッチ（apps/generator/ac-mens-hub-daily）と Hub API（apps/api/ac-mens-hub）が同じ日付・日時を扱う
 * （オファー内訳タブの日付・最終計算日時・計算し直す期間・BQ で置き換える範囲など）。片方だけで別の実装を持つと、
 * 暦に無い日・存在しない時刻の扱いや月またぎの加減が食い違いうるため、ここを唯一の実装にする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 暦の日付（YYYY-MM-DD）の書式・実在の判定と日数の加減、日本時間の日時の読み書きを、日次バッチと Hub API で共有する
 * @graph-connects none
 */
/**
 * 日付の書式（`YYYY-MM-DD`）。書式だけを見る（暦にある日かは `isCalendarDate` で見る）。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日付の文字列の書式を定義する
 * @graph-connects none
 */
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * `YYYY-MM-DD` の書式で、暦に実在する日か（'2026-09-31' や、うるう年でない年の 2 月 29 日は false）。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 文字で書かれた日付が、書式どおりで暦にある日かを判定する
 * @graph-connects DATE_RE [references] 日付の書式
 */
export function isCalendarDate(text) {
    if (!DATE_RE.test(text))
        return false;
    const time = Date.parse(`${text}T00:00:00Z`);
    return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === text;
}
/**
 * `YYYY-MM-DD` に日数を足す（負なら引く）。UTC で計算するので実行環境のタイムゾーンに左右されず、
 * 月・年をまたいでも暦どおりに進む。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 暦の日付を前後にずらす
 * @graph-connects none
 */
export function addDays(date, days) {
    const day = new Date(`${date}T00:00:00Z`);
    day.setUTCDate(day.getUTCDate() + days);
    return day.toISOString().slice(0, 10);
}
/** 日本時間の日時の書式（'YYYY-MM-DD HH:mm:ss'）。 @graph-connects none */
const JST_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
/** 日本時間と UTC の差。 @graph-connects none */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
/**
 * 時刻（UTC のミリ秒）を日本時間の 'YYYY-MM-DD HH:mm:ss' にする。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 時刻を日本時間の日時の文字列にする
 * @graph-connects none
 */
export function formatJstDateTime(time) {
    return new Date(time + JST_OFFSET_MS).toISOString().slice(0, 19).replace('T', ' ');
}
/**
 * 日本時間の日時（'YYYY-MM-DD HH:mm:ss'）を時刻（UTC のミリ秒）にする。書式が違う・暦に無い日・存在しない時刻
 * （'25:00:00' など）なら null。Date.parse は暦に無い日を翌月へ繰り越すことがあるので、日本時間に戻して元の文字列と
 * 一致するかで確かめる。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日本時間で書かれた日時（スプレッドシートの最終計算日時など）を時刻に直す。書式違い・暦に無い日・存在しない時刻は読めないとする
 * @graph-connects formatJstDateTime [calls] 日本時間に戻して確かめる
 */
export function parseJstDateTime(text) {
    if (!JST_DATE_TIME_RE.test(text))
        return null;
    const time = Date.parse(`${text.replace(' ', 'T')}+09:00`);
    if (Number.isNaN(time))
        return null;
    return formatJstDateTime(time) === text ? time : null;
}
//# sourceMappingURL=calendar-date.js.map